import { arcSweep, walkPath } from './arcFit'
import type { Arc2D, Circle2D, Point2, Polyline2D } from './viewTypes'

// ---------------------------------------------------------------------------
// Área e perímetro de fronteiras COM ARCOS.
//
// O `measure.ts` faz a fórmula do sapateiro sobre os pontos clicados, e
// isso é o certo para quem clica pontos. Para quem ESCOLHE geometria do
// desenho já não chega: desde que o IR reconhece arcos e círculos
// (`arcFit.ts`), os `points` de uma polilinha redonda são só a
// tesselação de origem, e o sapateiro sobre eles SUBESTIMA sempre a área
// (o polígono inscrito cabe dentro do círculo). Num pilar de raio 0,25 m
// tesselado a 32 lados o erro é de 0,6 %; a 16 lados passa de 2 %.
//
// A matemática aqui é o integral de área, que é ADITIVO ao longo do
// caminho:
//
//   A = 1/2 · ∮ (x dy - y dx)
//
// Para um troço RECTO de P para Q vale 1/2·(Px·Qy - Qx·Py), que somado
// ao longo de um polígono fechado é exactamente o sapateiro. Para um
// troço de ARCO de centro (cx, cy), raio r e ângulos θ0 → θ1 vale
//
//   1/2·[cx·r·(sin θ1 - sin θ0) - cy·r·(cos θ1 - cos θ0) + r²·Δ]
//
// com Δ = θ1 - θ0 COM SINAL. Subtraindo a corda do mesmo troço, os
// termos do centro cancelam-se todos e sobra
//
//   1/2·r²·(Δ - sin Δ)
//
// que é a área do SEGMENTO CIRCULAR (a "barriga" entre a corda e o
// arco), independente de onde o círculo está. Como Δ - sin Δ é uma
// função ÍMPAR, o sinal sai sozinho do sentido do arco: um arco directo
// (anti-horário, Δ > 0) soma, um retrógrado subtrai. Não é preciso
// deduzir "bojo para fora ou para dentro" caso a caso, nem comparar o
// arco com o sentido de percurso da fronteira: percorrer a fronteira ao
// contrário troca o sinal de TUDO (sapateiro e segmentos), e o valor
// absoluto no fim é o mesmo.
//
// Donde a receita implementada:
//
//   área com sinal = sapateiro sobre os vértices das CORDAS
//                  + Σ 1/2·r²·(Δ - sin Δ) de cada arco
//
// Um CÍRCULO completo não passa por aqui: tem caminho próprio, π·r².
// (Passaria: dois semicírculos dão sapateiro 0 e dois segmentos de
// π·r²/2. Mas o caminho próprio é exacto por construção e não depende
// de o IR ter partido o círculo em dois arcos.)
//
// Perímetro: comprimento de ARCO (r·|Δ|) nos troços curvos, corda nos
// rectos. Medir a corda de um arco é o mesmo erro da área, do outro
// lado.
//
// FUROS: a região de um elemento pode ter mais do que um contorno
// fechado (o vazio de um pilar tubular, o negativo de uma conduta). É o
// mesmo problema que o poché já resolve no `drawingToSvg`, e resolve-se
// com a MESMA regra: par-ímpar. Lá é o `fill-rule="evenodd"` a fazê-lo
// no renderer; aqui, que é aritmética e não pintura, conta-se o
// ANINHAMENTO de cada anel (quantos outros anéis o contêm) e soma-se
// com sinal + a profundidade par e - a ímpar. Para contornos que não se
// cruzam (e os de um corte não se cruzam) as duas regras dão exactamente
// a mesma região.
//
// Puro: números, sem DOM, sem React, sem three.
// ---------------------------------------------------------------------------

/** Passo de uma fronteira. Ao contrário do `PathStep` do `arcFit`, não
 *  carrega índices para os `points`: uma fronteira pode ser invertida e
 *  encadeada com outras, e aí os índices deixam de querer dizer nada. */
export type BoundaryStep =
  | { kind: 'line'; to: Point2 }
  | { kind: 'arc'; arc: Arc2D; to: Point2 }

/** Caminho no plano do desenho, em metros. `closed` diz que o último
 *  passo volta ao `from` (a fronteira delimita uma área). */
export interface Boundary {
  from: Point2
  steps: BoundaryStep[]
  closed: boolean
  /** Círculo completo: caminho próprio (área e perímetro exactos). */
  circle?: Circle2D
}

/** Duas pontas mais próximas do que isto são a mesma ponta. Um
 *  micrómetro, como no `snap.ts`: muito abaixo de qualquer tolerância de
 *  construção e muito acima do ruído da vírgula flutuante. */
export const JOIN_EPS = 1e-6

/** Passo angular da amostragem de arcos quando é preciso devolver
 *  PONTOS (contorno de uma cota, teste de aninhamento). 11,25 graus: um
 *  círculo completo dá 32 pontos, que é o que a tesselação de origem já
 *  costuma ter e desenha-se liso. */
export const SAMPLE_STEP_RAD = Math.PI / 16

/** Amplitude COM SINAL de um arco: positiva no sentido directo. */
export function arcDelta(arc: Arc2D): number {
  const s = arcSweep(arc)
  return arc.ccw ? s : -s
}

/** Área do segmento circular de um arco, com sinal: 1/2·r²·(Δ - sin Δ).
 *  É o que há a somar ao sapateiro da corda para a área ficar exacta. */
export function arcSegmentArea(arc: Arc2D): number {
  const d = arcDelta(arc)
  return 0.5 * arc.r * arc.r * (d - Math.sin(d))
}

/** Comprimento de arco: r·|Δ|. */
export function arcLength(arc: Arc2D): number {
  return arc.r * arcSweep(arc)
}

/** Arco percorrido ao contrário. Os índices `start`/`end` deixam de ter
 *  significado (não há polilinha por trás) e vão a zero de propósito. */
export function reverseArc(arc: Arc2D): Arc2D {
  return {
    cx: arc.cx,
    cy: arc.cy,
    r: arc.r,
    start: 0,
    end: 0,
    a0: arc.a1,
    a1: arc.a0,
    ccw: !arc.ccw,
  }
}

function samePoint(a: Point2, b: Point2, eps = JOIN_EPS): boolean {
  return Math.hypot(a[0] - b[0], a[1] - b[1]) <= eps
}

/** Fronteira de uma polilinha do desenho. Um loop fechado leva o passo
 *  de fecho EXPLÍCITO (o `walkPath` deixa-o de fora porque quem escreve
 *  SVG/PDF o faz com o seu próprio operador; aqui não há operador
 *  nenhum, há aritmética). */
export function boundaryFromPolyline(pl: Polyline2D): Boundary | null {
  if (!pl || !Array.isArray(pl.points) || pl.points.length < 2) return null
  if (pl.circle) {
    const c = pl.circle
    return {
      from: [c.cx + c.r, c.cy],
      steps: [],
      closed: true,
      circle: { cx: c.cx, cy: c.cy, r: c.r },
    }
  }
  const walk = walkPath(pl)
  if (!walk) return null
  const steps: BoundaryStep[] = walk.steps.map((s) =>
    s.kind === 'arc'
      ? { kind: 'arc' as const, arc: s.arc, to: s.to }
      : { kind: 'line' as const, to: s.to },
  )
  const from: Point2 = [walk.from[0], walk.from[1]]
  if (pl.closed) {
    const end = steps.length > 0 ? steps[steps.length - 1].to : from
    if (!samePoint(end, from)) steps.push({ kind: 'line', to: from })
  }
  return { from, steps, closed: !!pl.closed }
}

/** Ponta final da fronteira. */
export function boundaryEnd(b: Boundary): Point2 {
  if (b.circle) return b.from
  return b.steps.length > 0 ? b.steps[b.steps.length - 1].to : b.from
}

/** Fronteira percorrida ao contrário. Um círculo completo é o mesmo
 *  círculo (a área não tem sentido de percurso). */
export function reverseBoundary(b: Boundary): Boundary {
  if (b.circle) return b
  const pts: Point2[] = [b.from, ...b.steps.map((s) => s.to)]
  const steps: BoundaryStep[] = []
  for (let i = b.steps.length - 1; i >= 0; i--) {
    const s = b.steps[i]
    const to = pts[i]
    steps.push(
      s.kind === 'arc'
        ? { kind: 'arc', arc: reverseArc(s.arc), to }
        : { kind: 'line', to },
    )
  }
  return { from: pts[pts.length - 1], steps, closed: b.closed }
}

/** Encadeia duas fronteiras abertas. Pressupõe que a ponta final da
 *  primeira coincide com o início da segunda (é quem chama que trata de
 *  inverter o que for preciso); se não coincidirem, o salto entra como
 *  um troço recto, que é o que uma fronteira com um buraco vale. */
export function joinBoundaries(a: Boundary, b: Boundary): Boundary {
  const steps = [...a.steps]
  if (!samePoint(boundaryEnd(a), b.from)) {
    steps.push({ kind: 'line', to: b.from })
  }
  steps.push(...b.steps)
  const out: Boundary = { from: a.from, steps, closed: false }
  out.closed = samePoint(boundaryEnd(out), out.from) && steps.length >= 2
  return out
}

/** Área COM SINAL (positiva no sentido directo). Uma fronteira aberta é
 *  fechada pela corda da ponta ao princípio, que é o que quem mede
 *  espera de um contorno quase fechado. */
export function signedBoundaryArea(b: Boundary): number {
  if (b.circle) return Math.PI * b.circle.r * b.circle.r
  let twice = 0
  let extra = 0
  let cur = b.from
  for (const s of b.steps) {
    twice += cur[0] * s.to[1] - s.to[0] * cur[1]
    if (s.kind === 'arc') extra += arcSegmentArea(s.arc)
    cur = s.to
  }
  // Corda de fecho (nula quando a fronteira já fecha).
  twice += cur[0] * b.from[1] - b.from[0] * cur[1]
  return twice / 2 + extra
}

/** Área, sempre positiva: o sentido de percurso não interessa a quem
 *  mede. */
export function boundaryArea(b: Boundary): number {
  return Math.abs(signedBoundaryArea(b))
}

/** Comprimento do caminho: arco nos troços curvos, corda nos rectos.
 *  Numa fronteira fechada é o perímetro (o passo de fecho já lá está). */
export function boundaryLength(b: Boundary): number {
  if (b.circle) return 2 * Math.PI * b.circle.r
  let total = 0
  let cur = b.from
  for (const s of b.steps) {
    total += s.kind === 'arc' ? arcLength(s.arc) : Math.hypot(s.to[0] - cur[0], s.to[1] - cur[1])
    cur = s.to
  }
  return total
}

/** Amostra a fronteira em pontos, com os arcos partidos em troços de
 *  `SAMPLE_STEP_RAD`. Num contorno fechado o último ponto NÃO repete o
 *  primeiro (é a convenção do `Dimension2D` e do `Polyline2D`). */
export function boundaryPoints(b: Boundary, stepRad = SAMPLE_STEP_RAD): Point2[] {
  const out: Point2[] = []
  const push = (p: Point2): void => {
    const last = out[out.length - 1]
    if (last && samePoint(last, p)) return
    out.push([p[0], p[1]])
  }
  if (b.circle) {
    const { cx, cy, r } = b.circle
    const n = Math.max(8, Math.ceil((2 * Math.PI) / Math.max(stepRad, 1e-3)))
    for (let i = 0; i < n; i++) {
      const a = (2 * Math.PI * i) / n
      out.push([cx + r * Math.cos(a), cy + r * Math.sin(a)])
    }
    return out
  }
  push(b.from)
  for (const s of b.steps) {
    if (s.kind === 'arc') {
      const d = arcDelta(s.arc)
      const n = Math.max(1, Math.ceil(Math.abs(d) / Math.max(stepRad, 1e-3)))
      for (let i = 1; i < n; i++) {
        const a = s.arc.a0 + (d * i) / n
        push([s.arc.cx + s.arc.r * Math.cos(a), s.arc.cy + s.arc.r * Math.sin(a)])
      }
    }
    push(s.to)
  }
  // Contorno fechado: o passo de fecho trouxe o primeiro ponto outra vez.
  if (out.length > 2 && samePoint(out[out.length - 1], out[0])) out.pop()
  return out
}

/** Medida de uma fronteira só. */
export interface BoundaryMeasure {
  area: number
  length: number
  closed: boolean
}

export function measureBoundary(b: Boundary): BoundaryMeasure {
  return {
    area: b.closed ? boundaryArea(b) : 0,
    length: boundaryLength(b),
    closed: b.closed,
  }
}

// --- Região com furos ------------------------------------------------------

/** Ponto dentro do polígono (lançamento de raio, regra par-ímpar). Os
 *  arcos não entram: para decidir quem está dentro de quem, a barriga de
 *  um arco nunca é o que decide, e a tesselação é a mesma que o desenho
 *  mostra. */
export function pointInPolygon(pts: Point2[], x: number, y: number): boolean {
  if (!pts || pts.length < 3) return false
  let inside = false
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [xi, yi] = pts[i]
    const [xj, yj] = pts[j]
    if (yi > y !== yj > y) {
      const t = (y - yi) / (yj - yi)
      if (x < xi + t * (xj - xi)) inside = !inside
    }
  }
  return inside
}

/** Resultado de uma região: um ou mais anéis fechados do mesmo sítio. */
export interface RegionMeasure {
  /** Área líquida: os furos já foram subtraídos. */
  area: number
  /** Perímetro de TODOS os anéis, furos incluídos (é o que um CAD
   *  reporta e é o comprimento de traço que ali está). */
  perimeter: number
  /** Anéis considerados. */
  loops: number
  /** Anéis que entraram a subtrair. */
  holes: number
  /** Anel exterior de maior área, amostrado em pontos (contorno da
   *  cota). */
  outline: Point2[]
  /** Os restantes anéis, amostrados (furos e outros exteriores). */
  rings: Point2[][]
}

/** Área e perímetro de um conjunto de contornos FECHADOS do mesmo sítio,
 *  com os furos a subtrair pela regra par-ímpar (ver o cabeçalho). */
export function regionMeasure(
  polys: Polyline2D[],
  stepRad = SAMPLE_STEP_RAD,
): RegionMeasure {
  const empty: RegionMeasure = {
    area: 0,
    perimeter: 0,
    loops: 0,
    holes: 0,
    outline: [],
    rings: [],
  }
  if (!Array.isArray(polys) || polys.length === 0) return empty
  const rings: { area: number; length: number; pts: Point2[] }[] = []
  for (const pl of polys) {
    if (!pl || !pl.closed) continue
    const b = boundaryFromPolyline(pl)
    if (!b) continue
    const pts = boundaryPoints(b, stepRad)
    if (pts.length < 3) continue
    rings.push({ area: boundaryArea(b), length: boundaryLength(b), pts })
  }
  if (rings.length === 0) return empty

  let area = 0
  let perimeter = 0
  let holes = 0
  for (let i = 0; i < rings.length; i++) {
    const probe = representativePoint(rings[i].pts, rings)
    let depth = 0
    for (let j = 0; j < rings.length; j++) {
      if (j === i) continue
      if (pointInPolygon(rings[j].pts, probe[0], probe[1])) depth++
    }
    const sign = depth % 2 === 0 ? 1 : -1
    if (sign < 0) holes++
    area += sign * rings[i].area
    perimeter += rings[i].length
  }

  const order = rings.map((_, i) => i).sort((a, b) => rings[b].area - rings[a].area)
  const outline = rings[order[0]].pts
  const others = order.slice(1).map((i) => rings[i].pts)
  return {
    area: Math.abs(area),
    perimeter,
    loops: rings.length,
    holes,
    outline,
    rings: others,
  }
}

/** Ponto do anel que serve para decidir aninhamento: um vértice basta
 *  (anéis de um corte não se cruzam), mas um vértice EM CIMA de outro
 *  anel deixava o lançamento de raio à sorte, por isso salta-se para o
 *  seguinte enquanto isso acontecer. */
function representativePoint(
  pts: Point2[],
  rings: { pts: Point2[] }[],
): Point2 {
  const limit = Math.min(pts.length, 8)
  for (let i = 0; i < limit; i++) {
    const p = pts[i]
    let onEdge = false
    for (const r of rings) {
      if (r.pts === pts) continue
      if (distanceToPolygon(r.pts, p[0], p[1]) < 1e-9) {
        onEdge = true
        break
      }
    }
    if (!onEdge) return p
  }
  return pts[0]
}

/** Distância de um ponto ao contorno (não ao interior) de um polígono. */
export function distanceToPolygon(pts: Point2[], x: number, y: number): number {
  let best = Infinity
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    best = Math.min(best, distanceToSegment(x, y, pts[j], pts[i]))
  }
  return best
}

/** Distância de um ponto a um segmento. */
export function distanceToSegment(
  x: number,
  y: number,
  a: Point2,
  b: Point2,
): number {
  const dx = b[0] - a[0]
  const dy = b[1] - a[1]
  const len2 = dx * dx + dy * dy
  if (len2 <= 0) return Math.hypot(x - a[0], y - a[1])
  let t = ((x - a[0]) * dx + (y - a[1]) * dy) / len2
  t = t < 0 ? 0 : t > 1 ? 1 : t
  return Math.hypot(x - (a[0] + t * dx), y - (a[1] + t * dy))
}
